/**
 * Unified citation formatter façade.
 *
 * Usage:
 *   import { formatBibliography, formatInline, sortForStyle } from '@/lib/citations/formatters';
 *
 *   const sorted = sortForStyle(refs, 'apa');
 *   const lines  = formatBibliography(sorted, 'apa');
 *   const label  = formatInline(ref, 'vancouver', 3);
 */

import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import { formatApaBibliography, formatApaInline, sortApa } from './apa';
import {
    formatChicagoBibliography,
    formatChicagoInline,
    sortChicago,
} from './chicago';
import {
    formatVancouverBibliography,
    formatVancouverInline,
    sortVancouver,
} from './vancouver';

export type CitationStyle = 'apa' | 'chicago' | 'vancouver';

/**
 * Format a single reference as a bibliography entry.
 *
 * @param ref   The reference to format.
 * @param style The citation style.
 * @param index 1-based order-of-appearance index (required for Vancouver, ignored for APA/Chicago).
 */
export function formatBibliographyEntry(
    ref: ReferenceItem,
    style: CitationStyle,
    index: number,
): string {
    switch (style) {
        case 'chicago':
            return formatChicagoBibliography(ref);
        case 'vancouver':
            return formatVancouverBibliography(ref, index);
        case 'apa':
        default:
            return formatApaBibliography(ref);
    }
}

/**
 * Format all references as a bibliography list (each entry on its own line).
 * The `refs` array is expected to already be sorted/ordered for the style
 * (use `sortForStyle` before calling this if needed).
 */
export function formatBibliography(
    refs: ReferenceItem[],
    style: CitationStyle,
): string[] {
    return refs.map((ref, i) => formatBibliographyEntry(ref, style, i + 1));
}

/**
 * Sort references according to the style's ordering rule:
 * - APA / Chicago → alphabetical by first author last name
 * - Vancouver     → order of appearance (no-op; caller manages ordering)
 */
export function sortForStyle(
    refs: ReferenceItem[],
    style: CitationStyle,
): ReferenceItem[] {
    switch (style) {
        case 'chicago':
            return sortChicago(refs);
        case 'vancouver':
            return sortVancouver(refs);
        case 'apa':
        default:
            return sortApa(refs);
    }
}

/**
 * Format an inline citation label for a given style.
 *
 * @param ref   The reference being cited.
 * @param style The citation style.
 * @param index 1-based order-of-appearance index (used for Vancouver "[N]").
 */
export function formatInline(
    ref: ReferenceItem,
    style: CitationStyle,
    index: number = 1,
): string {
    switch (style) {
        case 'chicago':
            return formatChicagoInline(ref);
        case 'vancouver':
            return formatVancouverInline(ref, index);
        case 'apa':
        default:
            return formatApaInline(ref);
    }
}

/**
 * Copy the formatted bibliography to the clipboard.
 */
export async function copyBibliography(
    refs: ReferenceItem[],
    style: CitationStyle,
): Promise<void> {
    const sorted = sortForStyle(refs, style);
    const text = formatBibliography(sorted, style).join('\n\n');
    await navigator.clipboard.writeText(text);
}

export { formatApaBibliography, formatApaInline, sortApa };
export { formatChicagoBibliography, formatChicagoInline, sortChicago };
export { formatVancouverBibliography, formatVancouverInline, sortVancouver };
